import { useState } from 'react';
import { Box, Menu, MenuItem, Typography, CircularProgress } from '@mui/material';
import type { SxProps } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTranslation } from 'react-i18next';

import type { PollyLanguage } from '@shared/interfaces/polly';
import { languageFlags, languageLabels } from '../constants';
import { SectionLabelText } from '../AssistantScriptDialog.styled';
import { AutoTranslateButton } from '../LanguageTabs/styled';
import { useModalTheme } from '@shared/components/AssistantEditorModals/context';
import { modalThemeTokens } from '@shared/components/AssistantEditorModals/theme';
import {
  LanguageListSection,
  LanguageListHeaderRow,
  LanguageListCountText,
  LanguageRowsList,
  LanguageRowOuter,
  LanguageRow,
  LanguageRowFlag,
  LanguageRowName,
  BaseInputWrap,
  BaseInput,
  BaseBadge,
  TranslationField,
  TranslateRowButton,
  ShowMoreButton,
  NoaPanelPreview,
  NoaPanelPreviewLabel,
  NoaPanelPreviewButton,
  NoTranslationCaption,
  STALE_BORDER_COLOR,
  STALE_BG_COLOR,
} from './styled';

const COLLAPSED_COUNT = 3;

interface ButtonLanguageEditorProps {
  effectiveBaseLanguage: PollyLanguage;
  nonBaseLangs: PollyLanguage[];
  activeLanguage: PollyLanguage;
  uiClickButtonLabel: string;
  uiClickButtonLabelTranslations: Partial<Record<PollyLanguage, string>>;
  uiClickButtonLabelStale: Partial<Record<PollyLanguage, boolean>>;
  isTranslating: boolean;
  onActiveLanguageChange: (lang: PollyLanguage) => void;
  onBaseLabelChange: (value: string) => void;
  onTranslationChange: (lang: PollyLanguage, value: string) => void;
  onTranslateLangs: (langs: PollyLanguage[]) => void;
}

const ButtonLanguageEditor = ({
  effectiveBaseLanguage,
  nonBaseLangs,
  activeLanguage,
  uiClickButtonLabel,
  uiClickButtonLabelTranslations,
  uiClickButtonLabelStale,
  isTranslating,
  onActiveLanguageChange,
  onBaseLabelChange,
  onTranslationChange,
  onTranslateLangs,
}: ButtonLanguageEditorProps) => {
  const { t } = useTranslation('common');
  const modalTheme = useModalTheme();
  const tokens = modalThemeTokens[modalTheme];
  const [expanded, setExpanded] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const rows: PollyLanguage[] = [effectiveBaseLanguage, ...nonBaseLangs];
  const visibleRows = expanded ? rows : rows.slice(0, COLLAPSED_COUNT);
  const hiddenCount = rows.length - COLLAPSED_COUNT;

  const translatedCount = nonBaseLangs.filter((lang) =>
    (uiClickButtonLabelTranslations[lang] ?? '').trim(),
  ).length;

  const translateDisabled = isTranslating || !uiClickButtonLabel.trim();

  const emptyOrStaleLangs = nonBaseLangs.filter(
    (lang) =>
      !(uiClickButtonLabelTranslations[lang] ?? '').trim() ||
      uiClickButtonLabelStale[lang],
  );

  const isBaseLang = activeLanguage === effectiveBaseLanguage;
  const ownPreviewText = isBaseLang
    ? uiClickButtonLabel
    : uiClickButtonLabelTranslations[activeLanguage] ?? '';
  const isFallback = !isBaseLang && !ownPreviewText.trim();
  const previewText = ownPreviewText || uiClickButtonLabel;

  // Selected rows already get a full outlined frame from LanguageRow itself —
  // boxing the field inside it too reads as visual clutter, so the field
  // drops to a plain bottom border while it's the active/selected row.
  const getTranslationFieldSx = (isStale: boolean, selected: boolean): SxProps => {
    const color = isStale ? STALE_BORDER_COLOR : 'var(--am-focus-border-color)';

    if (selected) {
      return {
        '& .MuiInputBase-root': {
          backgroundColor: isStale
            ? `${STALE_BG_COLOR} !important`
            : 'transparent !important',
        },
        '& .MuiOutlinedInput-notchedOutline': {
          border: 'none !important',
          borderRadius: '0 !important',
          borderBottom: `1px solid ${color} !important`,
        },
      };
    }

    return {
      '& .MuiInputBase-root': {
        backgroundColor: isStale ? `${STALE_BG_COLOR} !important` : undefined,
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: isStale
          ? `${STALE_BORDER_COLOR} !important`
          : 'var(--am-border-mid) !important',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: isStale
          ? `${STALE_BORDER_COLOR} !important`
          : 'var(--am-border-heavy) !important',
      },
      '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: `${color} !important`,
      },
    };
  };

  return (
    <Box>
      <LanguageListSection>
        <LanguageListHeaderRow>
          <SectionLabelText sx={{ mb: 0 }}>
            {t('assistant.scriptDialog.language')}{' '}
            <LanguageListCountText component="span">
              · {t('assistant.scriptDialog.wizard.languagesProgress', {
                translated: translatedCount,
                total: nonBaseLangs.length,
              })}
            </LanguageListCountText>
          </SectionLabelText>
          <Box sx={{ display: 'flex', flexShrink: 0 }}>
            <AutoTranslateButton
              variant="contained"
              size="small"
              data-testid="script-button-label-translate-auto-btn"
              sx={{
                borderRadius: '8px 0 0 8px !important',
                borderRight: 'none !important',
              }}
              onClick={() => onTranslateLangs(emptyOrStaleLangs)}
              disabled={translateDisabled}
              startIcon={
                isTranslating ? (
                  <CircularProgress size={13} color="inherit" />
                ) : undefined
              }
            >
              {isTranslating
                ? t('assistant.scriptDialog.translating')
                : t('assistant.scriptDialog.autoTranslateEmpty')}
            </AutoTranslateButton>
            <AutoTranslateButton
              variant="contained"
              size="small"
              aria-label={t('assistant.scriptDialog.moreTranslateOptions')}
              sx={{
                borderRadius: '0 8px 8px 0 !important',
                px: '6px !important',
                minWidth: 'unset',
              }}
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              disabled={translateDisabled}
            >
              <ArrowDropDownIcon sx={{ fontSize: 18 }} />
            </AutoTranslateButton>
          </Box>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{
              paper: {
                sx: {
                  borderRadius: 2,
                  mt: 0.5,
                  minWidth: 220,
                  background: tokens.surface,
                  border: tokens.inlineBtnBorder,
                  '& .MuiMenuItem-root': {
                    color: tokens.inlineBtnColor,
                    '&:hover': { backgroundColor: tokens.inlineBtnHoverBg },
                  },
                },
              },
            }}
          >
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                onTranslateLangs(emptyOrStaleLangs);
              }}
              data-testid="script-button-label-translate-menu-empty"
              sx={{ gap: 1.5, alignItems: 'flex-start', py: 1 }}
            >
              <Typography
                sx={{ fontSize: 13, fontWeight: 500, color: tokens.menuAccentColor }}
              >
                {t('assistant.scriptDialog.translateEmptyAndOutdated')}
              </Typography>
            </MenuItem>
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                onTranslateLangs(nonBaseLangs);
              }}
              data-testid="script-button-label-translate-menu-all"
              sx={{ gap: 1.5, alignItems: 'flex-start', py: 1 }}
            >
              <Typography
                sx={{ fontSize: 13, fontWeight: 500, color: tokens.menuAccentColor }}
              >
                {t('assistant.scriptDialog.reTranslateAllOverwrite')}
              </Typography>
            </MenuItem>
          </Menu>
        </LanguageListHeaderRow>

        <LanguageRowsList>
          {visibleRows.map((lang) => {
            const isBase = lang === effectiveBaseLanguage;
            const selected = activeLanguage === lang;
            const isStale = !isBase && Boolean(uiClickButtonLabelStale[lang]);
            const translationValue = isBase
              ? uiClickButtonLabel
              : uiClickButtonLabelTranslations[lang] ?? '';

            return (
              <LanguageRowOuter key={lang}>
                <LanguageRow
                  $selected={selected}
                  onClick={() => onActiveLanguageChange(lang)}
                >
                  <LanguageRowFlag>{languageFlags[lang]}</LanguageRowFlag>
                  <LanguageRowName $base={isBase}>
                    {languageLabels[lang]}
                  </LanguageRowName>
                  {isBase ? (
                    <BaseInputWrap
                      $selected={selected}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <BaseInput
                        placeholder={t('assistant.scriptDialog.buttonLabel')}
                        value={uiClickButtonLabel}
                        inputProps={{
                          'data-testid': 'script-button-label-input-base',
                        }}
                        onFocus={() => onActiveLanguageChange(lang)}
                        onChange={(e) => onBaseLabelChange(e.target.value)}
                      />
                      <BaseBadge>
                        {t('assistant.scriptDialog.baseBadge')}
                      </BaseBadge>
                    </BaseInputWrap>
                  ) : (
                    <TranslationField
                      size="small"
                      fullWidth
                      placeholder={t('assistant.scriptDialog.enterTranslation', {
                        language: languageLabels[lang],
                      })}
                      value={translationValue}
                      inputProps={{
                        title: isStale
                          ? t('assistant.scriptDialog.staleTranslationHint')
                          : undefined,
                        'data-testid': `script-button-label-input-${lang}`,
                      }}
                      sx={getTranslationFieldSx(isStale, selected)}
                      onFocus={() => onActiveLanguageChange(lang)}
                      onChange={(e) => onTranslationChange(lang, e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                </LanguageRow>
                {isBase ? (
                  <Box sx={{ width: 32, flexShrink: 0 }} />
                ) : (
                  <TranslateRowButton
                    aria-label={t(
                      'assistant.scriptDialog.autoTranslateLanguageAria',
                      { language: languageLabels[lang] },
                    )}
                    title={t(
                      'assistant.scriptDialog.autoTranslateLanguageAria',
                      { language: languageLabels[lang] },
                    )}
                    disabled={translateDisabled}
                    onClick={() => onTranslateLangs([lang])}
                    data-testid={`script-button-label-translate-${lang}`}
                  >
                    ✨
                  </TranslateRowButton>
                )}
              </LanguageRowOuter>
            );
          })}
        </LanguageRowsList>

        {hiddenCount > 0 && (
          <ShowMoreButton
            onClick={() => setExpanded((prev) => !prev)}
            data-testid="script-button-label-show-more-btn"
          >
            {expanded ? (
              <>
                {t('assistant.scriptDialog.showFewerLanguages')}
                <ExpandLessIcon sx={{ fontSize: 18 }} />
              </>
            ) : (
              <>
                {t('assistant.scriptDialog.showMoreLanguages', {
                  count: hiddenCount,
                })}
                <ExpandMoreIcon sx={{ fontSize: 18 }} />
              </>
            )}
          </ShowMoreButton>
        )}
      </LanguageListSection>

      <NoaPanelPreview>
        <NoaPanelPreviewLabel>
          {t('assistant.scriptDialog.previewInNoaPanel')}
        </NoaPanelPreviewLabel>
        <NoaPanelPreviewButton $isFallback={isFallback}>
          {previewText || t('assistant.scriptDialog.buttonLabel')}
        </NoaPanelPreviewButton>
        <NoTranslationCaption sx={{ visibility: isFallback ? 'visible' : 'hidden' }}>
          {t('assistant.scriptDialog.noTranslationYetShowingBase')}
        </NoTranslationCaption>
      </NoaPanelPreview>
    </Box>
  );
};

export default ButtonLanguageEditor;
